import * as enabledApi from '@shared/api/enabledPlayApi';
import INotificationModel from '@shared/models/users/NotificationModel';
import { useState } from 'react';
import { createContainer } from 'unstated-next';

function useNotificationContainer() {
    const [notifications, setNotifications] = useState<INotificationModel[]>([]);
    const [unreadNotifications, setUnreadNotifications] = useState<INotificationModel[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const initializeFromRemote = async () => {
        getUserNotificationsAll();
    };

    const getUserNotificationsUnread = async () => {
        setLoading(true);
        try {
            const allNotes = await enabledApi.getUserNotificationsUnread();
            if (allNotes?.resultType === 'Ok' && allNotes.data) {
                setUnreadNotifications(allNotes.data.filter((n) => !n.isRead));
            } else {
                setErrors(['Failed to load notifications']);
                console.error('Error initializing notifications:', allNotes);
            }
        } catch (e) {
            setErrors([e.message]);
            console.error('Error initializing notifications:', e);
        }
        setLoading(false);
    };

    const getUserNotificationsAll = async () => {
        setLoading(true);
        try {
            const allNotes = await enabledApi.getUserNotificationsAll();
            if (allNotes?.resultType === 'Ok' && allNotes.data) {
                setNotifications(allNotes.data);
                const unreadNotes = allNotes.data.filter((n) => !n.isRead);
                setUnreadNotifications(unreadNotes);
            } else {
                setErrors(['Failed to load notifications']);
                console.error('Error initializing notifications:', allNotes);
            }
        } catch (e) {
            setErrors([e.message]);
            console.error('Error initializing notifications:', e);
        }
        setLoading(false);
    };

    const markNotificationRead = async (notificationId: string) => {
        setLoading(true);
        try {
            const markResult = await enabledApi.markNotificationRead(notificationId);
            if (markResult?.resultType == 'Ok' && markResult.data) {
                const newNotes = [...notifications];
                const noteIndex = newNotes.findIndex((n) => n.id === notificationId);
                if (noteIndex > -1) {
                    newNotes[noteIndex] = markResult.data;
                    setNotifications(newNotes);
                    setUnreadNotifications(newNotes.filter((n) => !n.isRead));
                }
            } else {
                setErrors(['Failed to mark notification as read']);
                console.error('Error marking notification as read:', markResult);
            }
        } catch (e) {
            setErrors([e.message]);
            console.error('Error marking notification as read:', e);
        }
    };

    const generateNotification = async (notification: INotificationModel) => {
        setLoading(true);
        try {
            const generateResult = await enabledApi.generateNotification(notification);
            if (generateResult?.resultType == 'Ok' && generateResult.data) {
                const newNotes = [...notifications];
                newNotes.push(generateResult.data);
                setNotifications(newNotes);
                setUnreadNotifications(newNotes.filter((n) => !n.isRead));
            } else {
                setErrors(['Failed to generate notification']);
                console.error('Error generating notification:', generateResult);
            }
        } catch (e) {
            setErrors([e.message]);
            console.error('Error generating notification:', e);
        }
        initializeFromRemote();
        setLoading(false);
    };

    return {
        initializeFromRemote,
        markNotificationRead,
        unreadNotifications,
        setUnreadNotifications,
        generateNotification,
        getUserNotificationsAll,
        getUserNotificationsUnread,
        notifications,
        setNotifications,
        errors,
        setErrors,
        loading,
        setLoading,
    };
}

const NotificationContainer = createContainer(useNotificationContainer);
export default NotificationContainer;
